import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, X } from 'lucide-react';
import api from '../utils/api';

export default function QuestionManager({ testSeriesId, testSeriesTitle, questions, onQuestionsUpdated }) {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [questionData, setQuestionData] = useState({
    question_text: '',
    question_type: 'mcq',
    options: ['', '', '', ''],
    correct_answer: ''
  });

  const resetForm = () => {
    setQuestionData({
      question_text: '',
      question_type: 'mcq',
      options: ['', '', '', ''],
      correct_answer: ''
    });
  };

  const handleChange = (field, value) => {
    setQuestionData(prev => ({ ...prev, [field]: value }));
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...questionData.options];
    newOptions[index] = value;
    setQuestionData(prev => ({ ...prev, options: newOptions }));
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        test_series_id: testSeriesId,
        question_text: questionData.question_text,
        question_type: questionData.question_type,
        options: questionData.question_type === 'mcq' ? questionData.options.filter(o => o.trim()) : null,
        correct_answer: questionData.correct_answer
      };

      await api.post('/admin/questions', payload);
      toast.success('Question added successfully!');
      resetForm();
      setShowAddDialog(false);
      onQuestionsUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add question');
    } finally {
      setLoading(false);
    }
  };

  const handleEditQuestion = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        question_text: questionData.question_text,
        question_type: questionData.question_type,
        options: questionData.question_type === 'mcq' ? questionData.options.filter(o => o.trim()) : null,
        correct_answer: questionData.correct_answer
      };

      await api.put(`/admin/questions/${editingQuestion.id}`, payload);
      toast.success('Question updated successfully!');
      setShowEditDialog(false);
      setEditingQuestion(null);
      resetForm();
      onQuestionsUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update question');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) {
      return;
    }

    try {
      await api.delete(`/admin/questions/${questionId}`);
      toast.success('Question deleted successfully');
      onQuestionsUpdated();
    } catch (error) {
      toast.error('Failed to delete question');
    }
  };

  const openEditDialog = (question) => {
    setEditingQuestion(question);
    setQuestionData({
      question_text: question.question_text,
      question_type: question.question_type,
      options: question.options || ['', '', '', ''],
      correct_answer: question.correct_answer
    });
    setShowEditDialog(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-outfit font-semibold">{testSeriesTitle}</h3>
          <p className="text-sm text-muted-foreground">{questions.length} questions</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setShowAddDialog(true);
          }}
          className="bg-primary hover:bg-primary/90 text-white font-semibold"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Question
        </Button>
      </div>

      {/* Questions List */}
      <div className="space-y-4">
        {questions.length === 0 ? (
          <div className="text-center py-12 bg-muted/30 rounded-lg border border-border">
            <p className="text-muted-foreground mb-4">No questions added yet</p>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add First Question
            </Button>
          </div>
        ) : (
          questions.map((q, index) => (
            <motion.div
              key={q.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-card border border-border rounded-lg p-6 hover:border-primary/30 transition-colors"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-lg font-bold text-primary font-mono">Q{index + 1}</span>
                  <span className="text-xs px-2 py-1 bg-accent/10 text-accent rounded-full font-semibold">
                    {q.question_type === 'mcq' ? 'MCQ' : 'Numerical'}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openEditDialog(q)}
                    data-testid={`edit-question-${q.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(q.id)}
                    className="text-destructive hover:text-destructive"
                    data-testid={`delete-question-${q.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <p className="font-manrope mb-4 text-lg">{q.question_text}</p>
              
              {q.options && (
                <div className="space-y-2 mb-4">
                  {q.options.map((opt, idx) => (
                    <div
                      key={idx}
                      className={`text-sm p-3 rounded-md border ${
                        opt === q.correct_answer
                          ? 'bg-success/10 border-success/30 text-success'
                          : 'bg-muted/30 border-border text-muted-foreground'
                      }`}
                    >
                      <span className="font-semibold">{String.fromCharCode(65 + idx)}.</span> {opt}
                    </div>
                  ))}
                </div>
              )}
              
              <div className="pt-3 border-t border-border">
                <span className="text-sm text-muted-foreground">Correct Answer: </span>
                <span className="text-sm font-semibold text-success">{q.correct_answer}</span>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Add Question Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-2xl font-outfit font-bold">Add New Question</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddQuestion} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="type">Question Type *</Label>
              <Select value={questionData.question_type} onValueChange={(value) => handleChange('question_type', value)}>
                <SelectTrigger className="bg-input/50 border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mcq">Multiple Choice (MCQ)</SelectItem>
                  <SelectItem value="numerical">Numerical Answer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="question">Question Text *</Label>
              <Textarea
                id="question"
                value={questionData.question_text}
                onChange={(e) => handleChange('question_text', e.target.value)}
                required
                placeholder="Enter the question..."
                rows={4}
                className="bg-input/50 border-border focus:border-primary"
              />
            </div>

            {questionData.question_type === 'mcq' && (
              <div className="space-y-4">
                <Label>Options *</Label>
                <div className="flex flex-col gap-4">
                  {questionData.options.map((option, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <span className="text-sm font-bold w-8 flex-shrink-0 text-primary">{String.fromCharCode(65 + index)}.</span>
                      <Input
                        value={option}
                        onChange={(e) => handleOptionChange(index, e.target.value)}
                        required
                        placeholder={`Option ${String.fromCharCode(65 + index)}`}
                        className="bg-input border-2 border-border focus:border-primary h-12"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="answer">Correct Answer *</Label>
              {questionData.question_type === 'mcq' ? (
                <Select value={questionData.correct_answer} onValueChange={(value) => handleChange('correct_answer', value)}>
                  <SelectTrigger className="bg-input/50 border-border">
                    <SelectValue placeholder="Select correct answer" />
                  </SelectTrigger>
                  <SelectContent>
                    {questionData.options.filter(o => o.trim()).map((option, index) => (
                      <SelectItem key={index} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="answer"
                  value={questionData.correct_answer}
                  onChange={(e) => handleChange('correct_answer', e.target.value)}
                  required
                  placeholder="Enter the correct numerical answer"
                  className="bg-input/50 border-border focus:border-primary"
                />
              )}
            </div>

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-primary hover:bg-primary/90 text-white font-semibold"
              >
                {loading ? 'Adding...' : 'Add Question'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Question Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-2xl font-outfit font-bold">Edit Question</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditQuestion} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="edit-type">Question Type *</Label>
              <Select value={questionData.question_type} onValueChange={(value) => handleChange('question_type', value)}>
                <SelectTrigger className="bg-input/50 border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mcq">Multiple Choice (MCQ)</SelectItem>
                  <SelectItem value="numerical">Numerical Answer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-question">Question Text *</Label>
              <Textarea
                id="edit-question"
                value={questionData.question_text}
                onChange={(e) => handleChange('question_text', e.target.value)}
                required
                rows={4}
                className="bg-input/50 border-border focus:border-primary"
              />
            </div>

            {questionData.question_type === 'mcq' && (
              <div className="space-y-4">
                <Label>Options *</Label>
                <div className="flex flex-col gap-4">
                  {questionData.options.map((option, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <span className="text-sm font-bold w-8 flex-shrink-0 text-primary">{String.fromCharCode(65 + index)}.</span>
                      <Input
                        value={option}
                        onChange={(e) => handleOptionChange(index, e.target.value)}
                        required
                        className="bg-input border-2 border-border focus:border-primary h-12"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="edit-answer">Correct Answer *</Label>
              {questionData.question_type === 'mcq' ? (
                <Select value={questionData.correct_answer} onValueChange={(value) => handleChange('correct_answer', value)}>
                  <SelectTrigger className="bg-input/50 border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {questionData.options.filter(o => o.trim()).map((option, index) => (
                      <SelectItem key={index} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="edit-answer"
                  value={questionData.correct_answer}
                  onChange={(e) => handleChange('correct_answer', e.target.value)}
                  required
                  className="bg-input/50 border-border focus:border-primary"
                />
              )}
            </div>

            <div className="flex gap-3">
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-primary hover:bg-primary/90 text-white font-semibold"
              >
                {loading ? 'Updating...' : 'Update Question'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowEditDialog(false);
                  setEditingQuestion(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
